import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";

const SPECS = [
    { v: "quantitative", l: "قدرات كمية" },
    { v: "verbal", l: "قدرات لفظية" },
    { v: "both", l: "كمية ولفظية" },
];

export default function Trainers() {
    const [rows, setRows] = useState([]);
    const [classes, setClasses] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ username: "", full_name: "", password: "", specialization: "quantitative", assigned_class_ids: [], can_view_messages: true, active: true });

    const load = async () => {
        try {
            const [t, c] = await Promise.all([api.get("/admin/trainers"), api.get("/classes")]);
            setRows(t.data); setClasses(c.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        try {
            await api.post("/admin/trainers", form);
            toast.success("تم إنشاء المدربة");
            setOpen(false);
            setForm({ username: "", full_name: "", password: "", specialization: "quantitative", assigned_class_ids: [], can_view_messages: true, active: true });
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (id) => {
        if (!window.confirm("حذف المدربة؟")) return;
        try { await api.delete(`/admin/trainers/${id}`); toast.success("تم الحذف"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const resetPw = async (id) => {
        try { const { data } = await api.post(`/admin/users/${id}/reset-password`, {}); toast.success(`كلمة المرور: ${data.new_password}`); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const toggleClass = (cid) => setForm((f) => ({
        ...f, assigned_class_ids: f.assigned_class_ids.includes(cid) ? f.assigned_class_ids.filter(x => x !== cid) : [...f.assigned_class_ids, cid]
    }));

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-[#0F172A]">المدربات</h1>
                    <p className="text-sm text-slate-500 mt-1">{rows.length} مدربة</p>
                </div>
                <Button onClick={() => setOpen(true)} data-testid="btn-add-trainer" className="bg-[#1B2A4E] hover:bg-[#0F172A]"><Plus className="w-4 h-4 ml-1" /> إضافة مدربة</Button>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                            <tr>
                                <th className="px-4 py-3 text-right">الاسم</th>
                                <th className="px-4 py-3 text-right">اسم المستخدم</th>
                                <th className="px-4 py-3 text-right">التخصص</th>
                                <th className="px-4 py-3 text-right">الفصول</th>
                                <th className="px-4 py-3 text-right">الحالة</th>
                                <th className="px-4 py-3 w-32"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-400">لا توجد مدربات — أضيفي أول مدربة</td></tr>}
                            {rows.map((r) => (
                                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                                    <td className="px-4 py-3 font-medium">{r.full_name}</td>
                                    <td className="px-4 py-3 font-mono-num" dir="ltr">{r.username}</td>
                                    <td className="px-4 py-3">{SPECS.find(s => s.v === r.specialization)?.l || r.specialization}</td>
                                    <td className="px-4 py-3 text-slate-600 text-xs">{r.assigned_class_ids?.length || 0} فصل</td>
                                    <td className="px-4 py-3">{r.active ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">نشط</Badge> : <Badge className="bg-red-100 text-red-800 hover:bg-red-100">موقوف</Badge>}</td>
                                    <td className="px-4 py-3 flex gap-1">
                                        <Button size="icon" variant="ghost" onClick={() => resetPw(r.id)} title="إعادة كلمة المرور"><KeyRound className="w-4 h-4" /></Button>
                                        <Button size="icon" variant="ghost" onClick={() => remove(r.id)} title="حذف" className="text-[#C8102E]"><Trash2 className="w-4 h-4" /></Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent dir="rtl" className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>إضافة مدربة جديدة</DialogTitle>
                        <DialogDescription>الحقول جميعها مطلوبة</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div><Label>الاسم الكامل</Label><Input data-testid="trainer-name-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>اسم المستخدم</Label><Input data-testid="trainer-username-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1 font-mono-num" dir="ltr" /></div>
                            <div><Label>كلمة المرور الأولية</Label><Input data-testid="trainer-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" /></div>
                        </div>
                        <div><Label>التخصص</Label>
                            <Select value={form.specialization} onValueChange={(v) => setForm({ ...form, specialization: v })}>
                                <SelectTrigger data-testid="trainer-spec-select" className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>{SPECS.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="mb-2 block">الفصول المسندة</Label>
                            <div className="max-h-40 overflow-auto border border-slate-200 rounded-lg p-2 space-y-1">
                                {classes.length === 0 && <div className="text-xs text-slate-400 p-2">لا توجد فصول بعد</div>}
                                {classes.map((c) => (
                                    <label key={c.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer">
                                        <Checkbox checked={form.assigned_class_ids.includes(c.id)} onCheckedChange={() => toggleClass(c.id)} />
                                        <span className="text-sm">{c.name}</span>
                                        <span className="text-xs text-slate-400 mr-auto">{c.student_count} طالبة</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Switch checked={form.can_view_messages} onCheckedChange={(v) => setForm({ ...form, can_view_messages: v })} />
                            <Label>يمكنها الاطلاع على الرسائل</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
                        <Button onClick={create} data-testid="btn-save-trainer" className="bg-[#1B2A4E] hover:bg-[#0F172A]">حفظ</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
